import { useEffect, useRef, useState } from "react";

type Options = {
  threshold?: number;
  rootMargin?: string;
  once?: boolean;
};

// Shared observer registry so many <Reveal>/<CountUp> instances share a single
// IntersectionObserver per (threshold, rootMargin) bucket instead of allocating
// one observer per element.
type Subscriber = (entry: IntersectionObserverEntry) => void;
const registries = new Map<string, { obs: IntersectionObserver; subs: Map<Element, Subscriber> }>();

function getRegistry(threshold: number, rootMargin: string) {
  const key = `${threshold}|${rootMargin}`;
  let entry = registries.get(key);
  if (entry) return entry;
  const subs = new Map<Element, Subscriber>();
  const obs = new IntersectionObserver(
    (entries) => {
      for (const e of entries) {
        const cb = subs.get(e.target);
        if (cb) cb(e);
      }
    },
    { threshold, rootMargin },
  );
  entry = { obs, subs };
  registries.set(key, entry);
  return entry;
}

export function useInView<T extends Element>({
  threshold = 0.12,
  rootMargin = "0px 0px -40px 0px",
  once = true,
}: Options = {}) {
  const ref = useRef<T | null>(null);
  const [inView, setInView] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    if (typeof IntersectionObserver === "undefined") {
      setInView(true);
      return;
    }
    const { obs, subs } = getRegistry(threshold, rootMargin);
    const cb: Subscriber = (entry) => {
      if (entry.isIntersecting) {
        setInView(true);
        if (once) {
          subs.delete(el);
          obs.unobserve(el);
        }
      } else if (!once) {
        setInView(false);
      }
    };
    subs.set(el, cb);
    obs.observe(el);
    return () => {
      subs.delete(el);
      obs.unobserve(el);
    };
  }, [threshold, rootMargin, once]);

  return { ref, inView };
}
